import { useCallback, useEffect, useRef, useState } from 'react';
import { FormatSegment } from '@app/app/_components/DatePicker/FormatSegment';
import { parseFormatToSegments } from '@app/app/_components/DatePicker/parseFormatToSegments';
import { getTotalDigitCount } from '@app/app/_components/DatePicker/getTotalDigitCount';
import { buildInputValue } from '@app/app/_components/DatePicker/buildInputValue';
import { parseRawDigitsToDate } from '@app/app/_components/DatePicker/parseRawDigitsToDate';
import { removeDigitsFromFormatted } from '@app/app/_components/DatePicker/removeDigitsFromFormatted';

interface UseDateInputMaskOptions {
    format: string;
    selectedDate: Nullable<Date>;
    minDate?: Date;
    maxDate?: Date;
    inputRef: React.RefObject<Nullable<HTMLInputElement>>;
    onDateCommit: (date: Date) => void;
    onDateClear: () => void;
}

interface UseDateInputMaskResult {
    inputValue: string;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleInputBlur: () => void;
    syncFromCalendar: (date: Nullable<Date>) => void;
}

export function useDateInputMask({
    format,
    selectedDate,
    minDate,
    maxDate,
    inputRef,
    onDateCommit,
    onDateClear,
}: UseDateInputMaskOptions): UseDateInputMaskResult {
    const segments = parseFormatToSegments(format);
    const totalDigits = getTotalDigitCount(segments);

    const segmentsRef = useRef<FormatSegment[]>(segments);
    const totalDigitsRef = useRef(totalDigits);
    const selectedDateRef = useRef(selectedDate);

    segmentsRef.current = segments;
    totalDigitsRef.current = totalDigits;
    selectedDateRef.current = selectedDate;

    const [rawDigits, setRawDigits] = useState<string>(() =>
        selectedDate
            ? removeDigitsFromFormatted(
                  buildInputValue(removeDigitsFromFormatted(selectedDate.toDateString()), segments),
              )
            : '',
    );

    const [cursorPosition, setCursorPosition] = useState<Nullable<number>>(null);

    const formatDateToRaw = useCallback(
        (date: Date): string => {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString();

            let result = format;
            result = result.replace('yyyy', year);
            result = result.replace('MM', month);
            result = result.replace('dd', day);

            return removeDigitsFromFormatted(result);
        },
        [format],
    );

    const inputValue = buildInputValue(rawDigits, segments);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            const newDigits = newValue.replace(/\D/g, '').slice(0, totalDigitsRef.current);

            setRawDigits(newDigits);

            const newInputValue = buildInputValue(newDigits, segmentsRef.current);
            setCursorPosition(newInputValue.length);

            if (newDigits.length === totalDigitsRef.current) {
                const parsed = parseRawDigitsToDate(newDigits, segmentsRef.current);

                if (parsed) {
                    const isBeforeMin =
                        minDate && parsed < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
                    const isAfterMax =
                        maxDate && parsed > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());

                    if (!isBeforeMin && !isAfterMax) {
                        onDateCommit(parsed);
                    }
                }
            }
        },
        [minDate, maxDate, onDateCommit],
    );

    const handleInputBlur = useCallback(() => {
        if (rawDigits.length < totalDigitsRef.current) {
            if (selectedDateRef.current) {
                setRawDigits(formatDateToRaw(selectedDateRef.current));
            } else {
                setRawDigits('');
                onDateClear();
            }
            return;
        }

        const parsed = parseRawDigitsToDate(rawDigits, segmentsRef.current);

        if (!parsed) {
            if (selectedDateRef.current) {
                setRawDigits(formatDateToRaw(selectedDateRef.current));
            } else {
                setRawDigits('');
                onDateClear();
            }
            return;
        }

        const isBeforeMin = minDate && parsed < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
        const isAfterMax = maxDate && parsed > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());

        if (isBeforeMin || isAfterMax) {
            if (selectedDateRef.current) {
                setRawDigits(formatDateToRaw(selectedDateRef.current));
            } else {
                setRawDigits('');
                onDateClear();
            }
        }
    }, [rawDigits, minDate, maxDate, formatDateToRaw, onDateClear]);

    const syncFromCalendar = useCallback(
        (date: Nullable<Date>) => {
            if (date) {
                setRawDigits(formatDateToRaw(date));
            } else {
                setRawDigits('');
            }
        },
        [formatDateToRaw],
    );

    useEffect(() => {
        if (cursorPosition === null) return;
        const input = inputRef.current;

        if (input) {
            input.setSelectionRange(cursorPosition, cursorPosition);
        }

        setCursorPosition(null);
    }, [cursorPosition, inputRef, inputValue]);

    return {
        inputValue,
        handleInputChange,
        handleInputBlur,
        syncFromCalendar,
    };
}
